import React, { useEffect, useState } from "react";
import { apiFetch, getApiBase, parseJsonOrExplain } from "./apiBase";

function ComboChart({ series }) {
  if (!series || series.length === 0) return null;

  const W = 520;
  const H = 220;
  const padL = 48;
  const padR = 44;
  const padT = 28;
  const padB = 52;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const years = series.map((r) => r.year || "–");
  const sales = series.map((r) => Number(r.net_sales_msek));
  const ebit = series.map((r) => Number(r.ebit_msek));
  const margins = series.map((r) =>
    r.ebit_margin_pct != null ? Number(r.ebit_margin_pct) : null
  );

  const maxBar = Math.max(...sales.map((v) => (Number.isFinite(v) ? v : 0)), 1);
  const finiteMargins = margins.filter((v) => v != null && Number.isFinite(v));
  const minM = finiteMargins.length ? Math.min(0, ...finiteMargins) : 0;
  const maxM = finiteMargins.length ? Math.max(0, ...finiteMargins) : 1;
  const spanM = Math.max(maxM - minM, 1);

  const n = series.length;
  const groupW = innerW / n;
  const barW = Math.min(36, (groupW * 0.22));
  const gap = 4;

  const barHeight = (v) => (Number.isFinite(v) ? (v / maxBar) * innerH : 0);
  const lineY = (pct) =>
    padT +
    innerH -
    (Number.isFinite(pct) ? ((pct - minM) / spanM) * innerH : 0);

  const linePoints = margins
    .map((pct, i) => {
      if (pct == null || !Number.isFinite(pct)) return null;
      const cx = padL + i * groupW + groupW / 2;
      const cy = lineY(pct);
      return `${cx},${cy}`;
    })
    .filter(Boolean)
    .join(" ");

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${W} ${H}`}
      style={{ maxWidth: "640px", display: "block" }}
      aria-label="Diagram omsättning, EBIT och EBIT-marginal"
    >
      <rect x="0" y="0" width={W} height={H} fill="#faf9f6" rx="8" />

      {[0, 0.5, 1].map((t) => {
        const y = padT + innerH * (1 - t);
        return (
          <g key={t}>
            <line
              x1={padL}
              x2={W - padR}
              y1={y}
              y2={y}
              stroke="rgba(17,17,17,0.08)"
              strokeWidth="1"
            />
            <text
              x={padL - 8}
              y={y + 4}
              textAnchor="end"
              fontSize="10"
              fill="#7a746c"
              fontFamily="Arial, Helvetica, sans-serif"
            >
              {(maxBar * t).toFixed(0)}
            </text>
          </g>
        );
      })}

      {series.map((row, i) => {
        const x0 = padL + i * groupW + groupW / 2 - barW - gap / 2;
        const x1 = padL + i * groupW + groupW / 2 + gap / 2;
        const h0 = barHeight(sales[i]);
        const h1 = barHeight(ebit[i]);
        const y0 = padT + innerH - h0;
        const y1 = padT + innerH - h1;
        return (
          <g key={i}>
            <rect
              x={x0}
              y={y0}
              width={barW}
              height={h0}
              fill="#1e3a5f"
              rx="2"
            />
            <rect
              x={x1}
              y={y1}
              width={barW}
              height={h1}
              fill="#7eb8da"
              rx="2"
            />
            <text
              x={padL + i * groupW + groupW / 2}
              y={H - 14}
              textAnchor="middle"
              fontSize="11"
              fill="#333"
              fontFamily="Arial, Helvetica, sans-serif"
            >
              {years[i]}
            </text>
          </g>
        );
      })}

      {linePoints && (
        <polyline
          fill="none"
          stroke="#5f5a52"
          strokeWidth="2"
          points={linePoints}
        />
      )}

      {margins.map((pct, i) => {
        if (pct == null || !Number.isFinite(pct)) return null;
        const cx = padL + i * groupW + groupW / 2;
        const cy = lineY(pct);
        return (
          <g key={`m${i}`}>
            <circle cx={cx} cy={cy} r="4" fill="#5f5a52" />
            <text
              x={cx}
              y={cy - 10}
              textAnchor="middle"
              fontSize="10"
              fill="#5f5a52"
              fontFamily="Arial, Helvetica, sans-serif"
            >
              {`${Math.round(pct)}%`}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default function CompanyOnePager({ companyId, companyName, omitDocumentHeader }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    const base = getApiBase();
    apiFetch(`${base}/companies/${encodeURIComponent(companyId)}/onepager`)
      .then((res) => parseJsonOrExplain(res))
      .then(setData)
      .catch((e) => setError(e.message || "Fel"))
      .finally(() => setLoading(false));
  }, [companyId]);

  const card = {
    background: "#fff",
    border: "1px solid rgba(17,17,17,0.10)",
    borderRadius: "18px",
    padding: "18px",
    marginBottom: "14px",
  };
  const companyDescription =
    data?.description || data?.website_summary || data?.business_overview || "";

  const formatMsek = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "–";
    return n.toLocaleString("sv-SE", { maximumFractionDigits: 1 });
  };

  const formatPct = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return `${n.toLocaleString("sv-SE", { maximumFractionDigits: 1 })}%`;
  };

  const derivedEbitdaMarginPct = (() => {
    const explicit = Number(data?.latest_ebitda_margin_pct);
    if (Number.isFinite(explicit)) return explicit;
    const ns = Number(data?.latest_net_sales_msek);
    const e = Number(data?.latest_ebitda_msek);
    if (!Number.isFinite(ns) || !Number.isFinite(e) || ns === 0) return null;
    return (e / ns) * 100;
  })();

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
      {!omitDocumentHeader && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            marginBottom: "16px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "11px",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: "#5f5a52",
                marginBottom: "6px",
              }}
            >
              Investment one-pager
            </div>
            <div style={{ fontSize: "22px", fontWeight: 500, fontFamily: "Georgia, serif" }}>
              {data?.company_name || companyName || companyId}
            </div>
          </div>
          <span
            style={{
              fontSize: "10px",
              letterSpacing: "0.15em",
              color: "#8a8278",
              border: "1px solid rgba(17,17,17,0.2)",
              padding: "6px 10px",
              borderRadius: "6px",
            }}
          >
            CONFIDENTIAL
          </span>
        </div>
      )}

      {loading && <p>Laddar…</p>}
      {error && <p style={{ color: "#b42318" }}>{error}</p>}

      {!loading && data && (
        <>
          <div style={{ marginBottom: "10px", textAlign: "left" }}>
            {data.logo_url ? (
              <img
                src={data.logo_url}
                alt={`${data.company_name || companyName || companyId} logo`}
                style={{ maxHeight: "54px", maxWidth: "220px", objectFit: "contain", display: "block" }}
              />
            ) : (
              <div
                style={{
                  fontSize: "11px",
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  color: "#8a8278",
                  border: "1px dashed rgba(17,17,17,0.2)",
                  borderRadius: "8px",
                  padding: "8px 10px",
                  display: "inline-block",
                }}
              >
                Ingen logo
              </div>
            )}
          </div>

          {data.data_warnings?.length > 0 && (
            <div
              style={{
                ...card,
                background: "#fff9f0",
                borderColor: "rgba(181, 71, 8, 0.25)",
                fontSize: "13px",
                lineHeight: 1.5,
              }}
            >
              {data.data_warnings.map((w, i) => (
                <div key={i}>{w}</div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
            <div style={{ flex: "1 1 320px", minWidth: "280px" }}>
              <div style={card}>
                <div
                  style={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: "#5f5a52",
                    marginBottom: "10px",
                  }}
                >
                  Översikt
                </div>
                <ul style={{ margin: 0, paddingLeft: "18px", lineHeight: 1.7, fontSize: "14px" }}>
                  <li>
                    <strong>Plats:</strong> {data.location_display || "–"}
                  </li>
                  <li>
                    <strong>VD:</strong>{" "}
                    {data.vd ? `${data.vd.name} (${data.vd.role})` : "–"}
                  </li>
                  <li>
                    <strong>Största ägare / koncern:</strong>{" "}
                    {data.largest_owner_display?.name || "–"}
                    {data.largest_owner_display?.orgnr && (
                      <span style={{ color: "#5f5a52" }}>
                        {" "}
                        (org.nr {data.largest_owner_display.orgnr})
                      </span>
                    )}
                  </li>
                  {data.largest_owner_stake_pct != null &&
                    data.largest_owner_stake_pct !== "" && (
                      <li>
                        <strong>Största ägarandel (universum):</strong>{" "}
                        {Number(data.largest_owner_stake_pct).toLocaleString("sv-SE")}%
                      </li>
                    )}
                  <li>
                    <strong>Omsättning (MSEK):</strong> {formatMsek(data.latest_net_sales_msek)}
                  </li>
                  <li>
                    <strong>EBITDA (MSEK):</strong> {formatMsek(data.latest_ebitda_msek)}
                    {formatPct(derivedEbitdaMarginPct) ? ` (${formatPct(derivedEbitdaMarginPct)})` : ""}
                  </li>
                </ul>
                {data.allabolag_url && (
                  <div style={{ marginTop: "12px", fontSize: "13px" }}>
                    <a href={data.allabolag_url} target="_blank" rel="noopener noreferrer">
                      Öppna på Allabolag.se
                    </a>
                  </div>
                )}
              </div>
              <div style={card}>
                <div
                  style={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: "#5f5a52",
                    marginBottom: "8px",
                  }}
                >
                  Bolagsbeskrivning
                </div>
                <div style={{ fontSize: "14px", lineHeight: 1.55, color: "#2f2b26", marginBottom: "10px" }}>
                  {companyDescription || "Beskrivning saknas i tillgängliga källor."}
                </div>
              </div>
            </div>

            <div style={{ flex: "1 1 360px", minWidth: "280px" }}>
              <div style={card}>
                <div
                  style={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: "#5f5a52",
                    marginBottom: "6px",
                  }}
                >
                  Finansiell utveckling
                </div>
                <p style={{ fontSize: "12px", color: "#5f5a52", margin: "0 0 12px 0", lineHeight: 1.45 }}>
                  Staplar: omsättning och <strong>EBIT</strong> (MSEK). Linje:{" "}
                  <strong>EBIT-marginal</strong> (%). EBIT hämtas som{" "}
                  <em>rörelseresultat efter avskrivningar</em> från Allabolags bokslut —{" "}
                  <strong>inte EBITDA</strong>.
                </p>
                <ComboChart series={data.financial_series} />
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "14px",
                    marginTop: "12px",
                    fontSize: "12px",
                    color: "#333",
                  }}
                >
                  <span>
                    <span
                      style={{
                        display: "inline-block",
                        width: "10px",
                        height: "10px",
                        background: "#1e3a5f",
                        marginRight: "6px",
                        verticalAlign: "middle",
                      }}
                    />
                    Omsättning
                  </span>
                  <span>
                    <span
                      style={{
                        display: "inline-block",
                        width: "10px",
                        height: "10px",
                        background: "#7eb8da",
                        marginRight: "6px",
                        verticalAlign: "middle",
                      }}
                    />
                    EBIT
                  </span>
                  <span>
                    <span
                      style={{
                        display: "inline-block",
                        width: "24px",
                        borderTop: "2px solid #5f5a52",
                        marginRight: "6px",
                        verticalAlign: "middle",
                      }}
                    />
                    EBIT-marginal %
                  </span>
                </div>
              </div>
            </div>
          </div>

          {data.operating_metric_sv && (
            <p style={{ fontSize: "11px", color: "#8a8278", marginTop: "12px", lineHeight: 1.5 }}>
              {data.operating_metric_sv}
            </p>
          )}
        </>
      )}
    </div>
  );
}
